<template>
  <div class="obra-editar">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Editar Obra</h1>
        <p class="content-header-subtitle">Modifica la información de la obra</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver" class="mr-2">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
        <b-button variant="primary" @click="guardarObra" :disabled="guardando">
          <feather-icon icon="SaveIcon" size="16" class="mr-50" />
          {{ guardando ? 'Guardando...' : 'Guardar' }}
        </b-button>
      </div>
    </div>

    <!-- Indicador de carga -->
    <div v-if="cargando" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando obra..."></b-spinner>
      <p class="mt-2 text-muted">Cargando información de la obra...</p>
    </div>

    <!-- Formulario de edición -->
    <b-card v-else-if="obra">
      <b-form @submit.prevent="guardarObra">
        <b-row>
          <!-- Información básica -->
          <b-col md="6">
            <b-form-group label="Dirección *" label-for="direccion">
              <b-form-input 
                id="direccion"
                v-model="obra.direccion" 
                type="text"
                required
                :state="validaciones.direccion"
              />
              <b-form-invalid-feedback v-if="!validaciones.direccion">
                La dirección es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="6">
            <b-form-group label="Referencia Externa" label-for="referenciaExterna">
              <b-form-input 
                id="referenciaExterna"
                v-model="obra.referenciaExterna" 
                type="text"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Municipio *" label-for="municipio">
              <b-form-input 
                id="municipio"
                v-model="obra.municipio" 
                type="text"
                required
                :state="validaciones.municipio"
              />
              <b-form-invalid-feedback v-if="!validaciones.municipio">
                El municipio es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="4">
            <b-form-group label="Provincia *" label-for="provincia">
              <b-form-input 
                id="provincia"
                v-model="obra.provincia" 
                type="text"
                required
                :state="validaciones.provincia"
              />
              <b-form-invalid-feedback v-if="!validaciones.provincia">
                La provincia es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="4">
            <b-form-group label="Código Postal *" label-for="codigoPostal">
              <b-form-input 
                id="codigoPostal"
                v-model="obra.codigoPostal" 
                type="number"
                required
                :state="validaciones.codigoPostal"
              />
              <b-form-invalid-feedback v-if="!validaciones.codigoPostal">
                El código postal es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Descripción" label-for="descripcion">
          <b-form-textarea 
            id="descripcion"
            v-model="obra.descripcion" 
            rows="3"
            placeholder="Descripción de la obra..."
          />
        </b-form-group>

        <!-- Información de la obra -->
        <b-row>
          <b-col md="6">
            <b-form-group label="Referencia de Obra" label-for="idReferenciaObra">
              <b-form-select 
                id="idReferenciaObra"
                v-model="obra.idReferenciaObra" 
                :options="opcionesReferenciasObras"
              >
                <template #first>
                  <b-form-select-option :value="null">Seleccionar referencia</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          
          <b-col md="6">
            <b-form-group label="Fase *" label-for="idFase">
              <b-form-select 
                id="idFase"
                v-model="obra.idFase" 
                :options="opcionesFases"
                required
                :state="validaciones.idFase"
              >
                <template #first>
                  <b-form-select-option :value="null">Seleccionar fase</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback v-if="!validaciones.idFase">
                La fase es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado de Obra *" label-for="idEstadoObra">
              <b-form-select 
                id="idEstadoObra"
                v-model="obra.idEstadoObra" 
                :options="opcionesEstadosObras"
                required
                :state="validaciones.idEstadoObra"
              >
                <template #first>
                  <b-form-select-option :value="null">Seleccionar estado</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback v-if="!validaciones.idEstadoObra">
                El estado es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="6">
            <b-form-group label="Tipo de Obra *" label-for="idTipoObra">
              <b-form-select 
                id="idTipoObra"
                v-model="obra.idTipoObra" 
                :options="opcionesTiposObras"
                required
                :state="validaciones.idTipoObra"
              >
                <template #first>
                  <b-form-select-option :value="null">Seleccionar tipo</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback v-if="!validaciones.idTipoObra">
                El tipo es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Fecha de Inicio *" label-for="fechaInicioObra">
              <b-form-input 
                id="fechaInicioObra"
                v-model="obra.fechaInicioObra" 
                type="date"
                required
                :state="validaciones.fechaInicioObra"
              />
              <b-form-invalid-feedback v-if="!validaciones.fechaInicioObra">
                La fecha de inicio es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="6">
            <b-form-group label="Fecha de Fin" label-for="fechaFinObra">
              <b-form-input 
                id="fechaFinObra"
                v-model="obra.fechaFinObra" 
                type="date"
                :state="validaciones.fechaFinObra"
              />
              <b-form-invalid-feedback v-if="!validaciones.fechaFinObra">
                La fecha de fin debe ser posterior a la fecha de inicio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Empresa *" label-for="idEmpresa">
              <b-form-select 
                id="idEmpresa"
                v-model="obra.idEmpresa" 
                :options="opcionesEmpresas"
                required
                :state="validaciones.idEmpresa"
              >
                <template #first>
                  <b-form-select-option :value="null">Seleccionar empresa</b-form-select-option>
                </template>
              </b-form-select>
              <b-form-invalid-feedback v-if="!validaciones.idEmpresa">
                La empresa es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          
          <b-col md="6">
            <b-form-group label="Magnitudes" label-for="magnitudes">
              <b-form-input 
                id="magnitudes"
                v-model="obra.magnitudes" 
                type="text"
                placeholder="Ej: 2,500 m² construidos"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Error si no se encuentra la obra -->
    <div v-else class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-warning mb-3"></feather-icon>
      <h4>Obra no encontrada</h4>
      <p class="text-muted">La obra que buscas no existe o ha sido eliminada.</p>
      <b-button variant="primary" @click="volver">
        Volver al listado
      </b-button>
    </div>
  </div>
</template>

<script>
import { ObraService } from '@/services/obraService'
import { ReferenciaObraService } from '@/services/referenciaObraService'
import { FaseService } from '@/services/faseService'
import { EstadoObraService } from '@/services/estadoObraService'
import { TipoObraService } from '@/services/tipoObraService'
import { EmpresaService } from '@/services/empresaService'
import { Obra, ReferenciaObra, Fase, EstadoObra, TipoObra, Empresa } from '@/types'

export default {
  name: 'ObraEditar',
  data() {
    return {
      obra: null,
      cargando: false,
      guardando: false,
      referenciasObras: [],
      fases: [],
      estadosObras: [],
      tiposObras: [],
      empresas: [],
      validaciones: {
        direccion: true,
        municipio: true,
        provincia: true,
        codigoPostal: true,
        idFase: true,
        idEstadoObra: true,
        idTipoObra: true,
        fechaInicioObra: true,
        fechaFinObra: true,
        idEmpresa: true
      }
    }
  },
  computed: {
    opcionesReferenciasObras() {
      return this.referenciasObras.map(ref => ({
        value: ref.idReferenciaObra,
        text: ref.nombre
      }))
    },
    opcionesFases() {
      return this.fases.map(fase => ({
        value: fase.idFase,
        text: fase.nombre
      }))
    },
    opcionesEstadosObras() {
      return this.estadosObras.map(estado => ({
        value: estado.idEstadoObra,
        text: estado.nombre
      }))
    },
    opcionesTiposObras() {
      return this.tiposObras.map(tipo => ({
        value: tipo.idTipoObra,
        text: tipo.nombre
      }))
    },
    opcionesEmpresas() {
      return this.empresas.map(empresa => ({
        value: empresa.idEmpresa,
        text: empresa.nombre
      }))
    }
  },
  async mounted() {
    await this.cargarCatalogos()
    await this.cargarObra()
  },
  methods: {
    async cargarObra() {
      const id = this.$route.params.id
      if (!id) {
        this.$router.push({ name: 'obras-lista' })
        return
      }

      this.cargando = true
      try {
        this.obra = await ObraService.getById(Number(id))
      } catch (error) {
        console.error('Error cargando obra:', error)
        this.$toast({
          component: { template: '<div>Error al cargar la obra</div>' },
          props: { variant: 'danger' },
        })
        this.obra = null
      } finally {
        this.cargando = false
      }
    },

    async cargarCatalogos() {
      try {
        const [referencias, fases, estados, tipos, empresas] = await Promise.all([
          ReferenciaObraService.getAll(),
          FaseService.getAll(),
          EstadoObraService.getAll(),
          TipoObraService.getAll(),
          EmpresaService.getAll()
        ])

        this.referenciasObras = referencias.sort((a, b) => a.nombre.localeCompare(b.nombre))
        this.fases = fases.sort((a, b) => a.nombre.localeCompare(b.nombre))
        this.estadosObras = estados.sort((a, b) => a.nombre.localeCompare(b.nombre))
        this.tiposObras = tipos.sort((a, b) => a.nombre.localeCompare(b.nombre))
        this.empresas = empresas.sort((a, b) => a.nombre.localeCompare(b.nombre))
      } catch (error) {
        console.error('Error cargando catálogos:', error)
      }
    },

    validarFormulario() {
      this.validaciones = {
        direccion: !!(this.obra && this.obra.direccion),
        municipio: !!(this.obra && this.obra.municipio),
        provincia: !!(this.obra && this.obra.provincia),
        codigoPostal: !!(this.obra && this.obra.codigoPostal),
        idFase: !!(this.obra && this.obra.idFase),
        idEstadoObra: !!(this.obra && this.obra.idEstadoObra),
        idTipoObra: !!(this.obra && this.obra.idTipoObra),
        fechaInicioObra: !!(this.obra && this.obra.fechaInicioObra),
        fechaFinObra: true, // Se valida por separado
        idEmpresa: !!(this.obra && this.obra.idEmpresa)
      }

      // Validar fechas
      if (this.obra && this.obra.fechaFinObra && this.obra.fechaInicioObra) {
        this.validaciones.fechaFinObra = new Date(this.obra.fechaInicioObra) < new Date(this.obra.fechaFinObra)
      }

      return Object.values(this.validaciones).every(v => v === true)
    },

    async guardarObra() {
      if (!this.validarFormulario()) {
        this.$toast({
          component: { template: '<div>Por favor, complete todos los campos obligatorios</div>' },
          props: { variant: 'warning' },
        })
        return
      }

      if (!this.obra) return

      this.guardando = true
      try {
        await ObraService.update(this.obra.idObra, this.obra)
        
        this.$toast({
          component: { template: '<div>Obra actualizada correctamente</div>' },
          props: { variant: 'success' },
        })
        
        this.$router.push({ name: 'obras-lista' })
      } catch (error) {
        console.error('Error guardando obra:', error)
        this.$toast({
          component: { template: '<div>Error al actualizar la obra</div>' },
          props: { variant: 'danger' },
        })
      } finally {
        this.guardando = false
      }
    },

    volver() {
      this.$router.push({ name: 'obras-lista' })
    }
  }
}
</script>

<style lang="scss" scoped>
.obra-editar {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

@media (max-width: 768px) {
  .obra-editar {
    padding: 1rem;
  }
  
  .content-header {
    flex-direction: column;
    align-items: stretch;
    
    .content-header-right {
      margin-top: 1rem;
    }
  }
  
  .content-header-title {
    font-size: 1.5rem;
  }
}
</style>
